import React, { useEffect, useMemo, useState } from "react";
import { FiCopy } from "react-icons/fi";
import QRCode from "react-qr-code";
import { RiErrorWarningFill } from "react-icons/ri";
import { BsArrowLeft } from "react-icons/bs";
import GetDepositAddy from "../../components/utils/GetDepositAddy";
import { truncateWord } from "../../utils/wordFunctions";
import { useUser } from "../../context/user-context";
import { PiSealWarningFill } from "react-icons/pi";
import { IoClose } from "react-icons/io5";
import CopyToClipboard from "../../components/utils/CopyToClipboard";
import { getNetworkLabel } from "../../constants/constants";
import usePublicWalletDetails from "../../hooks/usePublicWalletDetails";
import { getSupportedNetworks } from "../../utils/asset-config";

const DepositModal = ({ setOpenDeposit, selectedCoinObj }: any) => {
  const { theme: userTheme } = useUser();
  const { data: walletDetails } = usePublicWalletDetails();

  const networkOptions = useMemo(
    () => getSupportedNetworks(walletDetails, selectedCoinObj?.abbr),
    [walletDetails, selectedCoinObj?.abbr],
  );

  const [depositNetworkName, setDepositNetworkName] = useState<string>("");

  useEffect(() => {
    if (networkOptions.length > 0 && !depositNetworkName) {
      setDepositNetworkName(networkOptions[0].abbr);
    }
  }, [networkOptions, depositNetworkName]);

  const walletAddy = GetDepositAddy(
    selectedCoinObj?.abbr,
    depositNetworkName || networkOptions[0]?.abbr || selectedCoinObj?.networks[0]?.name
  );

  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(5);

  useEffect(() => {
    // Check if user has previously chosen to not show the disclaimer
    const shouldShowDisclaimer =
      localStorage.getItem("hideWalletDepositDisclaimer") !== "true";
    if (shouldShowDisclaimer) {
      setShowDisclaimer(true);
      setTimeLeft(5);
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setShowDisclaimer(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (showDisclaimer && timeLeft > 0) {
      const countdownTimer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
      return () => clearInterval(countdownTimer);
    }
  }, [showDisclaimer, timeLeft]);

  const handleDontShowAgain = () => {
    localStorage.setItem("hideWalletDepositDisclaimer", "true");
    setShowDisclaimer(false);
  };

  return (
    <div className="fixed inset-0  flex font-manrope justify-start items-start pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95  backdrop-blur-sm">
      <div
        className={` w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white  rounded-xl mx-auto p-6 dark:bg-[#192025]   lgss:w-2/5 xxl:w-1/3 `}
      >
        {showDisclaimer && (
          <div className="inset-0 fixed  bg-black bg-opacity-80">
            <div className="absolute bottom-[40%] left-1/2 font-sora transform border-l-4 border-[#DF4E4E] -translate-x-1/2 w-11/12 max-w-sm bg-white dark:bg-[#1C2227] rounded-r-xl shadow-lg p-4 transition-all duration-300 ease-in-out">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2 p-2 bg-[#DF4E4E] rounded-lg">
                  <h4 className="text-white font-semibold text-[14px]">
                    Minimum Deposit Required
                  </h4>
                </div>
                <button
                  onClick={() => setShowDisclaimer(false)}
                  className="text-gray-500"
                >
                  <IoClose className="text-xl" />
                </button>
              </div>
              <p className="text-gray-700 dark:text-gray-300 text-[13px] mb-3">
                To avoid losses, ensure that you deposit a minimum of $10 worth
                to the {selectedCoinObj?.abbr.toUpperCase()} wallet displayed
                above.
              </p>
              <div className="flex justify-between flex-row-reverse items-center">
                <button
                  onClick={handleDontShowAgain}
                  className="text-secondary_button text-sm underline"
                >
                  Don't show again
                </button>
                <div className="text-xs text-gray-500">0:0{timeLeft}</div>
              </div>
            </div>
          </div>
        )}
        <div className="w-full flex justify-start gap-6 items-center">
          <button
            onClick={() => {
              setOpenDeposit(false);
            }}
          >
            <BsArrowLeft className="text-black dark:text-white text-[20px]" />
          </button>
          <h4 className="text-gray-900 dark:text-gray-100 font-sora font-semibold text-[20px]">
            Receive {selectedCoinObj?.name}
          </h4>
        </div>
        <div className="w-ful mt-8">
          {walletAddy && (
            <div className="w-ful flex justify-center items-center">
              <QRCode
                size={160}
                bgColor={
                  userTheme === "light"
                    ? "#F1F1F1"
                    : userTheme === "dark"
                    ? "#1F1F1F"
                    : ""
                }
                fgColor={
                  userTheme === "light"
                    ? "#1D2739"
                    : userTheme === "dark"
                    ? "#E4E7EC"
                    : ""
                }
                value={walletAddy ?? ""}
              />
            </div>
          )}
          <h4 className="text-gray-500 dark:text-gray-400 text-[13px] mt-8 text-center">
            Your {selectedCoinObj?.abbr} Address
          </h4>
          <div className="flex justify-between text-[12px] mt-6 text-gray-800 dark:text-gray-200  items-center border bg-transparent border-gray-200 dark:border-gray-600 rounded-xl px-4 h-[52px]">
            <h4> {walletAddy ? truncateWord(walletAddy) : ""}</h4>
            <CopyToClipboard text={walletAddy}>
              <FiCopy className="text-[16px]  text-gray-800 dark:text-gray-200" />
            </CopyToClipboard>
          </div>
          <select
            name=""
            value={depositNetworkName}
            onChange={(e) => setDepositNetworkName(e.target.value)}
            className="flex outline-none w-full justify-between text-[12px] mt-6 text-gray-800 dark:text-gray-200  items-center border bg-transparent border-gray-200 dark:border-gray-600 rounded-xl px-4 h-[52px]"
            id=""
          >
            {networkOptions.map((net, index) => (
              <option key={index} value={net.abbr}>
                {net.name}
              </option>
            ))}
          </select>
          <div className="flex justify-center items-start py-3  gap-4 px-4 bg-[#E8F3FC] dark:bg-[#9AD1F9] w-full  mt-6  rounded-lg">
            <div>
              <RiErrorWarningFill className="text-[30px] text-secondary_button" />
            </div>
            <h4 className="text-[13px]">
              To avoid losing your fund, please ensure you’re sending{" "}
              <span className="font-bold"> {selectedCoinObj?.abbr}</span> from a{" "}
              <span className="font-bold">
                {" "}
                {depositNetworkName
                  ? getNetworkLabel(depositNetworkName)
                  : networkOptions[0]?.name || getNetworkLabel(selectedCoinObj?.networks[0]?.name)}
                {"  "}network
              </span>{" "}
              above
            </h4>
          </div>
          <div className="flex z-50 justify-start bg-[#FDE8E8] w-full p-4 items-center rounded-xl mt-4 gap-3">
            <div>
              <PiSealWarningFill className="text-[24px] text-[#DF4E4E]" />
            </div>

            <div>
              <h4 className=" text-[#DF4E4E]    text-[13px] font-semibold">
                Minimum Deposit is $10 worth worth of {selectedCoinObj?.abbr}
              </h4>
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            setOpenDeposit(false);
          }}
          className="mt-6 bg-secondary_button flex justify-center items-center text-white text-[15px] font-medium h-[48px] w-full rounded-xl"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default DepositModal;
